import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/Select";
import { Textarea } from "../ui/Textarea";
import { Truck, Loader2, MapPin, Calendar, CheckCircle } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useCreateBooking } from "../../hooks/useBooking";
import { bookingService } from "../../services";
import { hospitalService } from "../../services";
import BookingTrackingCard from "./BookingTrackingCard";
import apiService from "../../services/apiService";
import { Badge } from "../ui/Badge";
import { getAmbulanceTypeAbbreviation } from "../../utils/ambulanceTypeHelper";
import {
  autoDetectContact,
  detectContactType,
  validatePhoneNumber,
  validateEmail,
} from "../../utils/contactDetection";
import {
  validateForm,
  validateField,
  extractBackendValidationErrors,
} from "../../utils/formValidation";
import SuccessModal from "../ui/SuccessModal";
import AmbulanceBookingConfirmationModal from "./AmbulanceBookingConfirmationModal";
import AutomaticAllocationModal from "./AutomaticAllocationModal";
import CountryCodeSelector from "../ui/CountryCodeSelector";
import LocationAutocomplete from "../ui/LocationAutocomplete";

// Helper function to validate location has coordinates
const isValidLocation = (location) => {
  return location && location.latitude && location.longitude;
};

// Helper function to extract booking ID from API response
const extractBookingId = (result) => {
  // Handle new hospital booking response structure: { success: true, data: { booking_id: "..." } }
  // Also handle legacy response structures
  return result?.data?.booking_id ||
    result?.data?.id ||
    result?.booking_id ||
    result?.id ||
    null;
};

// Helper function to check if payment is online
const isOnlinePayment = (paymentType) => {
  return paymentType === "Card" || paymentType === "UPI" || paymentType === "ONLINE";
};

// Helper function to get hospital name for success modal
const getHospitalName = (user, selectedPartnerHospitalId, partnerHospitals, selectedHospitalId, hospitalId, availableHospitals, hospitalData) => {
  if (user?.role === "TENANT_ADMIN") {
    if (selectedPartnerHospitalId) {
      const ph = partnerHospitals.find(
        (h) => (h.hospital_id || h.id) === selectedPartnerHospitalId
      );
      return ph?.name || "Fleet Operator Booking";
    }
    return "Fleet Operator Booking";
  }
  if (user?.role === "SUPER_ADMIN" && availableHospitals.length > 0) {
    const hospital = availableHospitals.find(
      (h) => h.hospital_id === (selectedHospitalId || hospitalId)
    );
    return hospital ? hospital.name : "Selected Hospital";
  }
  if (hospitalData) {
    return hospitalData.name;
  }
  return "Your Hospital";
};

// Helper function to get ambulance type label
const getAmbulanceTypeLabel = (ambulanceTypes, ambulanceTypeValue) => {
  const type = ambulanceTypes.find(
    (t) => t.value === ambulanceTypeValue
  );
  return type ? type.label : "Selected Ambulance";
};

// Helper function to build success data
const buildSuccessData = (bookingId, formData, user, selectedPartnerHospitalId, partnerHospitals, selectedHospitalId, hospitalId, availableHospitals, hospitalData, ambulanceTypes, estimatedFare) => {
  return {
    bookingId: bookingId,
    patientName: formData.patientName,
    hospitalName: getHospitalName(user, selectedPartnerHospitalId, partnerHospitals, selectedHospitalId, hospitalId, availableHospitals, hospitalData),
    ambulanceType: getAmbulanceTypeLabel(ambulanceTypes, formData.ambulanceType),
    estimatedFare: estimatedFare,
    partnerPricingHint: user?.role === "TENANT_ADMIN" && selectedPartnerHospitalId ? "Partner rates applied" : undefined,
    contactPhone: formData.contactPhone,
    contactPhone_country_code: formData.contactPhone_country_code,
    contactEmail: formData.contactEmail,
    pickupAddress: formData.pickupAddress,
    destinationAddress: formData.destinationAddress,
    bookingType: formData.bookingType,
    direction: formData.direction,
  };
};

// Helper function to handle payment redirect
const handlePaymentRedirect = (bookingId) => {
  const token = localStorage.getItem('aps_jwt_token') || localStorage.getItem('token');
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
  const redirectUrl = window.location.origin;
  
  const paymentTestUrl = new URL('/payment-test', window.location.origin);
  paymentTestUrl.searchParams.set('bookingId', bookingId);
  paymentTestUrl.searchParams.set('token', token || '');
  paymentTestUrl.searchParams.set('apiUrl', apiBaseUrl);
  paymentTestUrl.searchParams.set('redirectUrl', redirectUrl);
  paymentTestUrl.searchParams.set('autoProcess', 'true');
  
  window.location.href = paymentTestUrl.toString();
};

// Helper function to extract estimated fare from response
const extractEstimatedFare = (result) => {
  // Handle new hospital booking response structure: { success: true, data: { estimated_fare: ... } }
  // Also handle legacy response structures
  return result?.data?.estimated_fare ||
    result?.data?.estimatedFare ||
    result?.estimated_fare ||
    result?.estimatedFare ||
    null;
};

// Helper function to handle booking errors
const handleBookingError = (err, setErrorState, setShowError, hideSuccessModal) => {
  if (err.status === 409 || err.stack?.includes("ConflictError")) {
    setErrorState(
      `? ${
        err.message ||
        "This booking conflicts with an existing booking. Please check if you already have an active booking for this patient."
      }`
    );
  } else {
    const errorInfo = extractBackendValidationErrors(err);
    if (
      errorInfo.errorType === "VALIDATION_ERROR" &&
      Object.keys(errorInfo.fieldErrors).length > 0
    ) {
      const fieldErrorMessages = Object.values(errorInfo.fieldErrors);
      setErrorState(
        `? ${errorInfo.generalMessage}: ${fieldErrorMessages.join(". ")}`
      );
    } else {
      setErrorState(`? ${err.message || errorInfo.generalMessage}`);
    }
  }
  setShowError(true);
  hideSuccessModal();
};

// Helper function to check if user is a hospital user
const isHospitalUser = (user) => {
  return user?.role === 'HOSPITAL_USER' || user?.role === 'HOSPITAL_ADMIN' || user?.role === 'hospital';
};

// Helper function to get hospital ID from dedicated localStorage key (Priority 1)
const getHospitalIdFromDedicatedKey = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const hospitalIdKey = 'aps_hospital_id';
    const hospitalIdFromKey = localStorage.getItem(hospitalIdKey);
    
    if (hospitalIdFromKey) {
      const finalHospitalId = String(hospitalIdFromKey);
      console.log('🏥 [buildBookingData] Hospital ID from dedicated key (PRIORITY 1):', finalHospitalId);
      return finalHospitalId;
    }
  } catch (error) {
    console.error('❌ [buildBookingData] Error getting hospital_id from dedicated key:', error);
  }

  return null;
};

// Helper function to get hospital ID from hospital_data (Priority 2)
const getHospitalIdFromHospitalData = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const hospitalDataKey = 'aps_hospital_data';
    const hospitalDataStr = localStorage.getItem(hospitalDataKey);
    
    if (!hospitalDataStr) {
      return null;
    }

    const hospitalData = JSON.parse(hospitalDataStr);
    const hospitalIdFromData = hospitalData.hospital_id || hospitalData.id;
    
    if (hospitalIdFromData) {
      const finalHospitalId = String(hospitalIdFromData);
      console.log('🏥 [buildBookingData] Hospital ID from hospital_data (PRIORITY 2):', finalHospitalId);
      
      // Store it in dedicated key for future use
      const hospitalIdKey = 'aps_hospital_id';
      localStorage.setItem(hospitalIdKey, finalHospitalId);
      return finalHospitalId;
    }
  } catch (e) {
    console.warn('⚠️ [buildBookingData] Failed to parse hospital_data:', e);
  }

  return null;
};

// Helper function to extract hospital ID from parsed user data
const extractHospitalIdFromUserData = (parsedUser) => {
  return parsedUser.hospital_id || 
         parsedUser.hospitalId || 
         parsedUser.hospital?.hospital_id ||
         parsedUser.hospital?.id ||
         parsedUser.id; // Sometimes hospital_id might be stored as id for hospital users
};

// Helper function to try alternative localStorage keys
const tryAlternativeLocalStorageKeys = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const alternativeKeys = ['user', 'userData', 'auth_user', 'currentUser'];
  const hospitalIdKey = 'aps_hospital_id';

  for (const key of alternativeKeys) {
    const altData = localStorage.getItem(key);
    if (!altData) {
      continue;
    }

    try {
      const parsed = JSON.parse(altData);
      const altHospitalId = parsed.hospital_id || parsed.hospitalId || parsed.hospital?.hospital_id;
      
      if (altHospitalId) {
        console.log(`🏥 [buildBookingData] Found hospital_id in alternative key "${key}":`, altHospitalId);
        const finalHospitalId = String(altHospitalId);
        localStorage.setItem(hospitalIdKey, finalHospitalId);
        return finalHospitalId;
      }
    } catch (e) {
      console.warn(`⚠️ [buildBookingData] Failed to parse data from key "${key}":`, e);
    }
  }

  return null;
};

// Helper function to get hospital ID from user_data (Priority 3)
const getHospitalIdFromUserData = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const userDataKey = 'aps_user_data';
  const userData = localStorage.getItem(userDataKey);
  
  if (!userData) {
    console.warn('⚠️ [buildBookingData] No user data found in localStorage with key:', userDataKey);
    return null;
  }

  try {
    const parsedUser = JSON.parse(userData);
    console.log('🏥 [buildBookingData] User data from localStorage:', parsedUser);
    
    const hospitalIdKey = 'aps_hospital_id';
    let finalHospitalId = extractHospitalIdFromUserData(parsedUser);
    
    if (finalHospitalId) {
      finalHospitalId = String(finalHospitalId);
      console.log('🏥 [buildBookingData] Hospital ID from localStorage user data (PRIORITY 3):', finalHospitalId);
      localStorage.setItem(hospitalIdKey, finalHospitalId);
      return finalHospitalId;
    }

    console.warn('⚠️ [buildBookingData] hospital_id not found in localStorage user data');
    console.warn('⚠️ [buildBookingData] Available keys:', Object.keys(parsedUser));
    
    return tryAlternativeLocalStorageKeys();
  } catch (error) {
    console.error('❌ [buildBookingData] Error parsing user data:', error);
    return null;
  }
};

// Helper function to get hospital ID from localStorage (all priorities)
const getHospitalIdFromLocalStorage = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    // Priority 1: Dedicated key
    const fromDedicatedKey = getHospitalIdFromDedicatedKey();
    if (fromDedicatedKey) {
      return fromDedicatedKey;
    }

    // Priority 2: Hospital data
    const fromHospitalData = getHospitalIdFromHospitalData();
    if (fromHospitalData) {
      return fromHospitalData;
    }

    // Priority 3: User data
    return getHospitalIdFromUserData();
  } catch (error) {
    console.error('❌ [buildBookingData] Error getting hospital_id from localStorage:', error);
    console.error('❌ [buildBookingData] Error details:', error.message);
    return null;
  }
};

// Helper function to get hospital ID from state parameter
const getHospitalIdFromState = (hospitalId) => {
  if (!hospitalId) {
    return null;
  }

  const finalHospitalId = String(hospitalId);
  console.log('🏥 [buildBookingData] Hospital ID from state parameter:', finalHospitalId);
  return finalHospitalId;
};

// Helper function to get hospital ID from user object
const getHospitalIdFromUser = (user) => {
  const userHospitalId = user.hospital_id || user.hospitalId;
  if (!userHospitalId) {
    return null;
  }

  const finalHospitalId = String(userHospitalId);
  console.log('🏥 [buildBookingData] Hospital ID from user object:', finalHospitalId);
  return finalHospitalId;
};

// Helper function to determine source of hospital ID
const determineHospitalIdSource = (finalHospitalId, hospitalId, user) => {
  if (typeof window !== 'undefined') {
    try {
      const userData = localStorage.getItem('aps_user_data');
      if (userData) {
        const parsed = JSON.parse(userData);
        const fromStorage = parsed.hospital_id || parsed.hospitalId;
        if (fromStorage === finalHospitalId) {
          return 'localStorage';
        }
      }
    } catch (e) {
      // Ignore errors
    }
  }

  if (hospitalId === finalHospitalId) {
    return 'state';
  }

  if ((user.hospital_id || user.hospitalId) === finalHospitalId) {
    return 'user object';
  }

  return 'unknown';
};

// Helper function to get hospital ID from localStorage for logging
const getHospitalIdFromLocalStorageForLogging = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const userData = localStorage.getItem('aps_user_data');
    if (userData) {
      const parsed = JSON.parse(userData);
      return parsed.hospital_id || parsed.hospitalId;
    }
  } catch (e) {
    // Ignore errors
  }

  return null;
};

// Helper function to log hospital ID source
const logHospitalIdSource = (finalHospitalId, hospitalId, user) => {
  const source = determineHospitalIdSource(finalHospitalId, hospitalId, user);
  
  console.log('🏥 [buildBookingData] Final Hospital ID for booking:', {
    source: source,
    fromLocalStorage: getHospitalIdFromLocalStorageForLogging(),
    fromState: hospitalId,
    fromUser: user.hospital_id || user.hospitalId,
    final: finalHospitalId,
    userRole: user.role
  });
};

// Helper function to resolve hospital ID for hospital users
const resolveHospitalIdForHospitalUser = (user, hospitalId) => {
  if (!isHospitalUser(user)) {
    return null;
  }

  // Priority 1: localStorage
  let finalHospitalId = getHospitalIdFromLocalStorage();
  
  // Priority 2: State parameter
  if (!finalHospitalId) {
    finalHospitalId = getHospitalIdFromState(hospitalId);
  }
  
  // Priority 3: User object
  if (!finalHospitalId) {
    finalHospitalId = getHospitalIdFromUser(user);
  }

  if (finalHospitalId) {
    logHospitalIdSource(finalHospitalId, hospitalId, user);
  }

  return finalHospitalId;
};

// Helper function to build location object
const buildLocationObject = (address, location) => {
  const locationObj = {
    address: address.trim(),
  };

  if (location) {
    locationObj.latitude = location.latitude;
    locationObj.longitude = location.longitude;
  }

  return locationObj;
};

// Helper function to build scheduled date time
const buildScheduledDateTime = (bookingType, scheduledDate, scheduledTime) => {
  if (bookingType !== "scheduled" || !scheduledDate || !scheduledTime) {
    return undefined;
  }

  return new Date(`${scheduledDate}T${scheduledTime}`).toISOString();
};

// Helper function to build booking data
const buildBookingData = (formData, user, hospitalId, selectedPartnerHospitalId, pickupLocation, destinationLocation, selectedFleetPartners) => {
  const finalHospitalId = resolveHospitalIdForHospitalUser(user, hospitalId);
  
  return {
    // Include hospital_id for hospital users (required for new API endpoint)
    // API expects snake_case: hospital_id (not camelCase: hospitalId)
    ...(user.role !== "TENANT_ADMIN" && finalHospitalId && { hospital_id: finalHospitalId }),
    ...(user.role === "TENANT_ADMIN" && selectedPartnerHospitalId && { partnerHospitalId: selectedPartnerHospitalId }),
    patientName: formData.patientName,
    patientPhone: formData.contactPhone.trim(),
    patientPhone_country_code: formData.contactPhone_country_code,
    patientAge: parseInt(formData.patientAge),
    patientGender: "OTHER",
    hmisId: formData.hmisId,
    pickupLocation: buildLocationObject(formData.pickupAddress, pickupLocation),
    dropoffLocation: buildLocationObject(formData.destinationAddress, destinationLocation),
    emergencyType: "NON_URGENT",
    priority: formData.emergencyLevel.toUpperCase(),
    bookingType: formData.bookingType.toUpperCase(),
    scheduledDateTime: buildScheduledDateTime(formData.bookingType, formData.scheduledDate, formData.scheduledTime),
    notes: formData.specialRequirements || undefined,
    contactPersonName: formData.relativeName || undefined,
    contactPersonPhone: formData.contactPhone.trim() || undefined,
    ...(selectedFleetPartners.length > 0 && { fleetPartnerIds: selectedFleetPartners }),
  };
};

const BookAmbulanceForm = ({
  isLoading: propIsLoading,
  setIsLoading,
  error: propError,
  setError,
}) => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();

  // Add state for hospital ID and hospital data
  const [hospitalId, setHospitalId] = useState(null);
  const [hospitalData, setHospitalData] = useState(null); // ADD THIS LINE
  const [loadingHospitalId, setLoadingHospitalId] = useState(false);

  // Fleet partner hospitals (for TENANT_ADMIN)
  const [partnerHospitals, setPartnerHospitals] = useState([]);
  const [selectedPartnerHospitalId, setSelectedPartnerHospitalId] = useState(null);
  const [loadingPartnerHospitals, setLoadingPartnerHospitals] = useState(false);

  // Add state for hospital selection (for SUPER_ADMIN)
  const [availableHospitals, setAvailableHospitals] = useState([]);
  const [selectedHospitalId, setSelectedHospitalId] = useState(null);

  // Add state for successful booking ID
  const [successfulBookingId, setSuccessfulBookingId] = useState(null);

  // Success and error message states
  const [successModal, setSuccessModal] = useState({
    isOpen: false,
    data: null,
  });
  const [showError, setShowError] = useState(false);

  // Auto-hide error after 5 seconds
  useEffect(() => {
    if (showError) {
      const timer = setTimeout(() => {
        setShowError(false);
      }, 5000); // 5 seconds

      // Cleanup timer if component unmounts or showError changes
      return () => clearTimeout(timer);
    }
  }, [showError]);

  // Confirmation modal state
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    data: null,
  });

  // Automatic allocation modal state
  const [automaticAllocationModal, setAutomaticAllocationModal] = useState({
    isOpen: false,
    bookingData: null,
    selectedAmbulance: null,
    availableDrivers: [],
  });

  // Add state for ambulance types
  const [ambulanceTypes, setAmbulanceTypes] = useState([]);
  const [loadingAmbulanceTypes, setLoadingAmbulanceTypes] = useState(false);

  // Add state for partner fleets (for hospital users)
  const [partnerFleets, setPartnerFleets] = useState([]);
  const [selectedFleetPartners, setSelectedFleetPartners] = useState([]);
  const [loadingPartnerFleets, setLoadingPartnerFleets] = useState(false);

  // Add form data state
  const [formData, setFormData] = useState({
    bookingType: "instant",
    ambulanceType: "",
    patientName: "",
    hmisId: "",
    patientAge: "0",
    relativeName: "",
    currentCondition: "",
    contactPhone: "",
    contactPhone_country_code: "+91", // Default to India
    contactEmail: "", // Add email field
    pickupAddress: "",
    destinationAddress: "",
    emergencyLevel: "normal",
    scheduledDate: "",
    scheduledTime: "",
    specialRequirements: "",
    fleetPartners: [], // Add fleet partners to form data
  });

  // Add state for field-level validation errors
  const [fieldErrors, setFieldErrors] = useState({});

  // Add state for location coordinates
  const [pickupLocation, setPickupLocation] = useState(null);
  const [destinationLocation, setDestinationLocation] = useState(null);

  // Add state for selected country (for Google Places autocomplete)
  const [selectedCountryIso, setSelectedCountryIso] = useState(null); // No country filter by default (global search)

  // Country code to ISO mapping
  const countryCodeToIso = {
    "+91": "in", // India
    "+1": "us", // United States/Canada
    "+44": "gb", // United Kingdom
    "+61": "au", // Australia
    "+49": "de", // Germany
    "+33": "fr", // France
    "+81": "jp", // Japan
    "+86": "cn", // China
    "+971": "ae", // UAE
    "+65": "sg", // Singapore
    "+55": "br", // Brazil
    "+39": "it", // Italy
    "+34": "es", // Spain
    "+7": "ru", // Russia
    "+82": "kr", // South Korea
    "+31": "nl", // Netherlands
    "+41": "ch", // Switzerland
    "+46": "se", // Sweden
    "+47": "no", // Norway
  };

  // Fetch ambulance types from API
  useEffect(() => {
    const fetchAmbulanceTypes = async () => {
      try {
        console.log('🚑 Starting to fetch ambulance types...');
        setLoadingAmbulanceTypes(true);

        const response = await apiService.getAmbulanceTypes();

        // Handle different response structures
        let types = [];
        if (response.success && response.data) {
          types = response.data;
        } else if (Array.isArray(response)) {
          types = response;
        }

        // Transform API data to match form structure
        const transformedTypes = types.map((type) => {
          // Create a proper display name, avoiding database IDs
          let displayName = type.name;

          // If the name looks like a database ID (UUID or similar), create a proper name
          if (
            (type.name && type.name.includes("-") && type.name.length > 20) ||
            type.name === type.type_id ||
            !type.name ||
            type.name.trim() === ""
          ) {
            // Generate a proper name based on type_id or use a default
            displayName = `Ambulance Type ${
              type.type_id ? type.type_id.slice(-4) : "Unknown"
            }`;
          }

          const shortName = type.abbreviation || getAmbulanceTypeAbbreviation(displayName) || displayName;
          return {
            value: type.type_id,
            label: shortName,
            fullName: displayName,
            price: `₹${type.base_fare}/km`,
            description: type.description || `${displayName} ambulance service`,
            per_km_rate: type.per_km_rate,
          };
        });

        console.log('🚑 Transformed ambulance types:', transformedTypes);
        setAmbulanceTypes(transformedTypes);

        // Don't auto-select ambulance type - let user choose to enable button
        // This ensures the "Book Ambulance" button starts disabled
      } catch (error) {
        // Fallback to default types
        const fallbackTypes = [
          {
            value: "fallback-1",
            label: "Basic Life Support (BLS)",
            price: "?200/km",
            description: "Basic Life Support ambulance service",
          },
          {
            value: "fallback-2",
            label: "Advanced Life Support (ALS)",
            price: "?350/km",
            description: "Advanced Life Support ambulance service",
          },
          {
            value: "fallback-3",
            label: "Critical Care",
            price: "?500/km",
            description: "Critical Care ambulance service",
          },
        ];
        console.log('🚑 Using fallback ambulance types:', fallbackTypes);
        setAmbulanceTypes(fallbackTypes);
        // Don't auto-select fallback type - let user choose to enable button
      } finally {
        console.log('🚑 Finished loading ambulance types');
        setLoadingAmbulanceTypes(false);
      }
    };

    fetchAmbulanceTypes();
  }, []);

  // Fetch hospital ID when component mounts or user changes
  useEffect(() => {
    const fetchHospitalId = async () => {
      // ADD: Wait for auth to finish loading before fetching hospital data
      if (authLoading) {
        return; // Don't fetch if auth is still loading
      }

      if (isAuthenticated && user) {
        try {
          setLoadingHospitalId(true);

          if (user.role === "SUPER_ADMIN") {
            // For SUPER_ADMIN, fetch all hospitals and use the first one as default
            const response = await hospitalService.getAllHospitals();
            if (response.data && response.data.length > 0) {
              setAvailableHospitals(response.data);
              setSelectedHospitalId(response.data[0].hospital_id);
              setHospitalId(response.data[0].hospital_id);
            } else {
              throw new Error("No hospitals found in the system");
            }
          } else if (user.role === "TENANT_ADMIN") {
            // For fleet operators (TENANT_ADMIN), they don't need hospital information
            // Set hospital_id to null for fleet operator bookings
            setHospitalId(null);
            setHospitalData(null);
            setLoadingHospitalId(false);
            return;
          } else if (user.operator_id) {
            // For other users (HOSPITAL_USER), get hospital by operator ID
            const response = await hospitalService.getHospitalByOperatorId(
              user.operator_id
            );
            if (response.data && response.data.hospital_id) {
              setHospitalId(response.data.hospital_id);
              setHospitalData(response.data);
            } else {
              throw new Error("No hospital found for this operator");
            }
          } else if (user.hospital_id) {
            // Fallback: Use hospital_id directly from user object (from localStorage)
            console.log('🏥 Using hospital_id from user object:', user.hospital_id);
            setHospitalId(user.hospital_id);
            // Try to get hospital data if available
            if (user.hospital) {
              setHospitalData(user.hospital);
            }
          } else {
            // Last resort: Try to get from localStorage
            try {
              const userData = localStorage.getItem('aps_user_data');
              if (userData) {
                const parsedUser = JSON.parse(userData);
                if (parsedUser.hospital_id || parsedUser.hospitalId) {
                  console.log('🏥 Using hospital_id from localStorage:', parsedUser.hospital_id || parsedUser.hospitalId);
                  setHospitalId(parsedUser.hospital_id || parsedUser.hospitalId);
                  if (parsedUser.hospital) {
                    setHospitalData(parsedUser.hospital);
                  }
                } else {
                  throw new Error("User is not associated with any operator or hospital");
                }
              } else {
                throw new Error("User is not associated with any operator or hospital");
              }
            } catch (error) {
              throw new Error("User is not associated with any operator or hospital");
            }
          }
        } catch (error) {
          setErrorState(
            `Unable to load hospital information: ${error.message}. Please contact support.`
          );
        } finally {
          setLoadingHospitalId(false);
        }
      }
    };

    fetchHospitalId();
    
    // Fetch partner fleets for hospital users
    if (isAuthenticated && (user?.role === "HOSPITAL_USER" || user?.role === "SUPER_ADMIN") && !authLoading) {
      fetchPartnerFleets();
    }

    // Fetch partner hospitals for fleet users (TENANT_ADMIN)
    if (isAuthenticated && user?.role === "TENANT_ADMIN" && !authLoading) {
      fetchPartnerHospitals();
    }
  }, [isAuthenticated, user, authLoading]); // ADD authLoading to dependencies

  // Fetch partner fleets
  const fetchPartnerFleets = async () => {
    try {
      setLoadingPartnerFleets(true);
      // TODO: Replace with actual API endpoint
      const response = await apiService.get('/hospital/partner-fleets?status=active');
      if (response.success && response.data) {
        setPartnerFleets(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching partner fleets:', error);
      // Don't show error, just silently fail - fleet selection is optional
    } finally {
      setLoadingPartnerFleets(false);
    }
  };

  // Fetch partner hospitals for fleet users
  const fetchPartnerHospitals = async () => {
    try {
      setLoadingPartnerHospitals(true);
      const response = await apiService.get('/fleet/partner-hospitals?status=active');
      if (response?.success && Array.isArray(response.data)) {
        setPartnerHospitals(response.data);
      } else if (Array.isArray(response)) {
        setPartnerHospitals(response);
      } else if (response?.data?.items && Array.isArray(response.data.items)) {
        setPartnerHospitals(response.data.items);
      }
    } catch (error) {
      // Silent fail; dropdown is optional
      console.error('Error fetching partner hospitals:', error);
    } finally {
      setLoadingPartnerHospitals(false);
    }
  };

  // This useEffect has been merged with the fetchHospitalId useEffect above

  // Remove the hardcoded ambulanceTypes array
  // const ambulanceTypes = [
  //   { value: "bls", label: "Basic Life Support (BLS)", price: "?200/km" },
  //   { value: "als", label: "Advanced Life Support (ALS)", price: "?350/km" },
  //   { value: "critical", label: "Critical Care", price: "?500/km" }
  // ]

  // Enhanced input change handler with real-time validation
  const handleInputChange = (field, value) => {
    if (field === "ambulanceType") {
      // Find the database ID for the selected label
      const selectedType = ambulanceTypes.find((type) => type.label === value);
      const finalValue = selectedType ? selectedType.value : value;
      
      console.log('🚑 Ambulance type selection debug:', {
        field,
        incomingValue: value,
        selectedType: selectedType?.label || 'not found',
        finalValue,
        ambulanceTypesAvailable: ambulanceTypes.length
      });
      
      setFormData((prev) => ({
        ...prev,
        [field]: finalValue,
      }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }

    // Real-time validation for immediate feedback
    if (value && value.trim()) {
      const fieldError = validateField(field, value, formData);
      setFieldErrors((prev) => ({
        ...prev,
        [field]: fieldError,
      }));
    } else {
      // Clear error when field is empty
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };
  // Get coordinates from address (mock implementation - replace with real geocoding)
  const getCoordinates = async (address) => {
    // TODO: Implement real geocoding service (Google Maps API, etc.)
    // For now, return mock coordinates
    return {
      latitude: 19.076 + (Math.random() - 0.5) * 0.1, // Mumbai area
      longitude: 72.8777 + (Math.random() - 0.5) * 0.1,
    };
  };
  // Use internal state if props are not provided
  const [internalError, setInternalError] = useState(null);

  // Use props if available, otherwise use internal state/hook state
  const isLoading = propIsLoading ?? false; // Removed creatingBooking as it's no longer used
  const error = propError ?? internalError;
  const setErrorState = setError ?? setInternalError;

  // Success modal handlers
  const showSuccessModal = useCallback((successData) => {
    setSuccessModal({
      isOpen: true,
      data: successData,
    });
    setShowError(false);
  }, []);

  // Get navigate function for routing
  const navigate = useNavigate();

  // Handle track booking from success modal - navigate to hospital dashboard (SAdminDashboard)
  const handleTrackBooking = useCallback(() => {
    const bookingId = successModal.data?.bookingId;
    if (bookingId) {
      // Close the success modal
      setSuccessModal({ isOpen: false, data: null });
      // Navigate to hospital dashboard to show SAdminDashboard (replaces image display)
      navigate(`/hospital?bookingId=${bookingId}`);
    }
  }, [successModal.data?.bookingId, navigate]);

  const hideSuccessModal = useCallback((resetForm = false) => {
    setSuccessModal({ isOpen: false, data: null });

    if (resetForm) {
      setFormData({
        bookingType: "instant",
        ambulanceType: "",
        patientName: "",
        hmisId: "",
        patientAge: "0",
        relativeName: "",
        currentCondition: "",
        contactPhone: "",
        contactPhone_country_code: "+91",
        pickupAddress: "",
        destinationAddress: "",
        emergencyLevel: "normal",
        specialRequirements: "",
        scheduledDate: "",
        scheduledTime: "",
        hospitalId: "",
      });
      // Reset location coordinates
      setPickupLocation(null);
      setDestinationLocation(null);
    }
  }, []);

  // Confirmation modal handlers
  const showConfirmationModal = useCallback((confirmationData) => {
    setConfirmationModal({
      isOpen: true,
      data: confirmationData,
    });
    setShowError(false);
  }, []);

  const hideConfirmationModal = useCallback(() => {
    setConfirmationModal({ isOpen: false, data: null });
  }, []);

  // Automatic allocation modal handlers
  const showAutomaticAllocationModal = useCallback((bookingData, selectedAmbulance, availableDrivers) => {
    setAutomaticAllocationModal({
      isOpen: true,
      bookingData,
      selectedAmbulance,
      availableDrivers,
    });
  }, []);

  const hideAutomaticAllocationModal = useCallback(() => {
    setAutomaticAllocationModal({
      isOpen: false,
      bookingData: null,
      selectedAmbulance: null,
      availableDrivers: [],
    });
  }, []);

  // Helper function to fetch available drivers based on filters
  const fetchAvailableDrivers = async (bookingData, selectedAmbulanceType) => {
    try {
      console.log('🔍 Fetching available drivers for automatic allocation...');
      console.log('📋 Booking criteria:', {
        ambulanceType: selectedAmbulanceType.label,
        pickupLocation: bookingData.pickupLocation,
        fleetPartnerIds: bookingData.fleetPartnerIds || [],
        hospitalId: bookingData.hospital_id
      });
      
      // TODO: Replace with actual API call to fetch drivers based on criteria:
      // - Selected Partner (Fleet Partner)
      // - Ambulance Type
      // - KM Range (within service area)
      // - Active status
      
      // Mock driver data for now - replace with actual API call
      const mockDrivers = [
        {
          id: 'driver-1',
          name: 'Rajesh Kumar',
          phone: '+91-9876543210',
          ambulanceNumber: 'MH-01-AB-1234',
          ambulanceType: selectedAmbulanceType.label,
          distance: 2.5,
          rating: 4.8,
          status: 'available',
          fleetPartnerId: 'fleet-1',
          location: {
            latitude: bookingData.pickupLocation.latitude + 0.01,
            longitude: bookingData.pickupLocation.longitude + 0.01
          }
        },
        {
          id: 'driver-2', 
          name: 'Suresh Patel',
          phone: '+91-9876543211',
          ambulanceNumber: 'MH-01-CD-5678',
          ambulanceType: selectedAmbulanceType.label,
          distance: 3.2,
          rating: 4.6,
          status: 'available',
          fleetPartnerId: 'fleet-2',
          location: {
            latitude: bookingData.pickupLocation.latitude + 0.02,
            longitude: bookingData.pickupLocation.longitude + 0.02
          }
        },
        {
          id: 'driver-3',
          name: 'Amit Singh',
          phone: '+91-9876543212', 
          ambulanceNumber: 'MH-01-EF-9012',
          ambulanceType: selectedAmbulanceType.label,
          distance: 4.1,
          rating: 4.9,
          status: 'available',
          fleetPartnerId: 'fleet-1',
          location: {
            latitude: bookingData.pickupLocation.latitude + 0.03,
            longitude: bookingData.pickupLocation.longitude + 0.03
          }
        }
      ];

      // Apply allocation criteria filters (in priority order):
      let filteredDrivers = mockDrivers;

      // 1. Filter by Selected Partner (Fleet Partner) if specified
      if (bookingData.fleetPartnerIds && bookingData.fleetPartnerIds.length > 0) {
        filteredDrivers = filteredDrivers.filter(driver => 
          bookingData.fleetPartnerIds.includes(driver.fleetPartnerId)
        );
        console.log('🏢 Filtered by fleet partners:', filteredDrivers.length, 'drivers remaining');
      }

      // 2. Filter by Ambulance Type (already matched in mock data)
      filteredDrivers = filteredDrivers.filter(driver => 
        driver.ambulanceType === selectedAmbulanceType.label
      );
      console.log('🚑 Filtered by ambulance type:', filteredDrivers.length, 'drivers remaining');

      // 3. Filter by KM Range (within service area - assume 50km max for now)
      const maxDistance = 50; // km
      filteredDrivers = filteredDrivers.filter(driver => 
        driver.distance <= maxDistance
      );
      console.log('📍 Filtered by distance range:', filteredDrivers.length, 'drivers remaining');

      // 4. Filter by Active status
      filteredDrivers = filteredDrivers.filter(driver => 
        driver.status === 'available'
      );
      console.log('✅ Filtered by active status:', filteredDrivers.length, 'drivers remaining');

      // 5. Sort by Nearest Distance (Ascending Order) - Priority requirement
      filteredDrivers.sort((a, b) => a.distance - b.distance);
      console.log('📊 Sorted by nearest distance (ascending)');
      
      console.log('✅ Final filtered and sorted drivers:', filteredDrivers);
      return filteredDrivers;
      
    } catch (error) {
      console.error('❌ Error fetching available drivers:', error);
      return [];
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent multiple submissions or submission during loading states
    if (isLoading || loadingHospitalId || loadingAmbulanceTypes) {
      return;
    }

    // Early validations
    if (!isAuthenticated || !user) {
      setErrorState("? Please login to book an ambulance");
      setShowError(true);
      return;
    }

    // For fleet operators (TENANT_ADMIN), hospital_id is not required
    if (!hospitalId && user.role !== "TENANT_ADMIN") {
      setErrorState(
        "? Unable to determine hospital. Please contact support or try refreshing the page."
      );
      setShowError(true);
      return;
    }

    // Validate form
    const validation = validateForm(
      formData,
      formData.bookingType,
      hospitalId,
      user?.role
    );
    if (!validation.isValid) {
      const errorMessages = Object.values(validation.errors);
      setErrorState(`? Please fix the following: ${errorMessages.join(". ")}`);
      setShowError(true);
      return;
    }

    // Clear previous errors
    setShowError(false);
    setErrorState(null);

    // Calculate estimated fare based on ambulance type
    const selectedAmbulanceType = ambulanceTypes.find(
      (t) => t.value === formData.ambulanceType
    );
    const estimatedFare = selectedAmbulanceType?.per_km_rate
      ? Math.round(selectedAmbulanceType.per_km_rate * 25) // Assuming 25km average distance
      : 12886; // Default fallback amount

    // Prepare confirmation modal data
    const confirmationData = {
      patientName: formData.patientName,
      hospitalName: (() => {
        if (user?.role === "TENANT_ADMIN") {
          if (selectedPartnerHospitalId) {
            const ph = partnerHospitals.find(
              (h) => (h.hospital_id || h.id) === selectedPartnerHospitalId
            );
            return ph?.name || "Fleet Operator Booking";
          }
          return "Fleet Operator Booking";
        }
        if (user?.role === "SUPER_ADMIN" && availableHospitals.length > 0) {
          const hospital = availableHospitals.find(
            (h) => h.hospital_id === (selectedHospitalId || hospitalId)
          );
          return hospital ? hospital.name : "Selected Hospital";
        }
        if (hospitalData) {
          return hospitalData.name;
        }
        return "Your Hospital";
      })(),
      ambulanceType: (() => {
        const type = ambulanceTypes.find(
          (t) => t.value === formData.ambulanceType
        );
        return type ? type.label : "Selected Ambulance";
      })(),
      estimatedFare: estimatedFare,
      partnerPricingHint: user?.role === "TENANT_ADMIN" && selectedPartnerHospitalId ? "Partner rates will be applied" : undefined,
      patientAge: formData.patientAge,
      phoneNumber: `${formData.contactPhone_country_code} ${formData.contactPhone}`,
      hmisId: formData.hmisId,
      pickupAddress: formData.pickupAddress,
      destinationAddress: formData.destinationAddress,
      currentCondition: formData.currentCondition,
      emergencyLevel: formData.emergencyLevel,
    };

    // Show confirmation modal instead of booking directly
    showConfirmationModal(confirmationData);
  };

  // Check for payment redirect on page load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('paymentStatus');
    const bookingId = urlParams.get('bookingId');
    const paymentId = urlParams.get('paymentId');

    if (paymentStatus === 'success' && bookingId) {
      // Payment was successful, verify payment status first, then fetch full booking details
      const fetchBookingDetails = async () => {
        try {
          console.log('Verifying payment status for booking:', bookingId);
          
          // First, verify the booking exists and payment status is PAID
          // New endpoint: /v1/hospital/bookings/:bookingId
          // Response structure: { success: true, message: "...", data: { booking_id: "...", status: "...", booking_type: "...", patient_name: "...", pickup_address: "...", dropoff_address: "...", created_at: "..." } }
          const bookingResponse = await bookingService.getBookingById(bookingId);
          
          console.log('📥 Booking details response:', bookingResponse);
          
          if (!bookingResponse.success || !bookingResponse.data) {
            if (setError) {
              setError('Booking not found. Payment may not have been processed correctly.');
            }
            return;
          }
          
          const booking = bookingResponse.data;
          
          // Check if payment status is actually PAID (if available in response)
          // New response structure may not include payment_status, so we check if it exists
          const actualPaymentStatus = booking.payment_status || booking.paymentStatus;
          if (actualPaymentStatus && actualPaymentStatus !== 'PAID' && actualPaymentStatus !== 'SUCCESS') {
            // Only show error if payment_status exists and is not PAID/SUCCESS
            // If payment_status is not in response, assume payment was successful (we're here after payment redirect)
            if (setError) {
              setError(`Payment verification failed. Payment status: ${actualPaymentStatus}. Please contact support.`);
            }
            return;
          }
          
          console.log('Payment verified. Fetching full booking details...');
          
          // Extract driver information
          const driverMatching = booking.driver_matching || {};
          const driverStatus = driverMatching?.success 
            ? driverMatching?.message || 'Driver assigned'
            : 'Waiting for driver assignment...';
          const matchingStatus = driverMatching?.success ? 'assigned' : 'searching';
          const driverName = driverMatching?.assigned_driver?.driver_name || 
                           booking.driver?.name || 
                           booking.vehicle?.assigned_driver?.name;

          // Extract fare information
          const estimatedFare = booking.estimated_fare || booking.estimatedFare || booking.final_fare || booking.finalFare;
          const finalFare = booking.final_fare || booking.finalFare || estimatedFare;

          // Handle new response structure: { booking_id, status, booking_type, patient_name, pickup_address, dropoff_address, created_at }
          // Also handle legacy response structures with nested objects
          const pickupAddress = booking.pickup_address || 
                               booking.pickup_location?.address || 
                               booking.pickupLocation?.address || 
                               booking.pickupAddress || 
                               'N/A';
          const destinationAddress = booking.dropoff_address ||
                                    booking.dropoff_location?.address || 
                                    booking.dropoffLocation?.address || 
                                    booking.destinationAddress || 
                                    'N/A';

          // Show success modal with full booking details
          if (showSuccessModal) {
            showSuccessModal({
              bookingId: booking.booking_id || booking.bookingId || bookingId,
              patientName: booking.patient_name || booking.patientName || 'N/A',
              hospitalName: booking.hospital?.name || booking.hospitalName || 'Selected Hospital',
              ambulanceType: booking.vehicle?.type?.name || booking.vehicle?.name || booking.ambulanceType || 'Ambulance',
              estimatedFare: estimatedFare,
              finalFare: finalFare,
              driverStatus: driverStatus,
              driverName: driverName,
              pickupAddress: pickupAddress,
              destinationAddress: destinationAddress,
              bookingType: booking.booking_type || booking.bookingType,
              direction: booking.journey_direction || booking.direction,
              matchingStatus: matchingStatus,
              paymentStatus: 'success',
              paymentId: paymentId,
              message: 'Payment completed successfully! Your booking is confirmed.'
            });
          }

          console.log('Booking details loaded:', booking);
        } catch (error) {
          console.error('Error fetching booking details after payment:', error);
          // Fallback if API call fails
          if (showSuccessModal) {
            showSuccessModal({
              bookingId: bookingId,
              paymentStatus: 'success',
              paymentId: paymentId,
              message: 'Payment completed successfully! Your booking is confirmed. (Unable to load full booking details)'
            });
          }
        }
      };

      fetchBookingDetails();

      // Clear URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [showSuccessModal]);

  // Helper function to validate and log location
  const validateAndLogLocation = (location, locationType) => {
    if (isValidLocation(location)) {
      return true;
    }

    console.error(`❌ ${locationType} location validation FAILED:`, {
      [`has${locationType}Location`]: !!location,
      hasLatitude: location?.latitude !== undefined,
      hasLongitude: location?.longitude !== undefined,
      [locationType.toLowerCase()]: location,
    });

    const message = locationType === 'Pickup'
      ? "⚠ Please select the Pickup Address from the dropdown suggestions. Don't just type - click on a suggestion!"
      : "⚠ Please select the Destination Address from the dropdown suggestions. Don't just type - click on a suggestion!";

    setErrorState(message);
    setShowError(true);
    return false;
  };

  // Helper function to try getting hospital ID from localStorage as last resort
  const tryGetHospitalIdFromLocalStorage = (bookingData) => {
    if (typeof window === 'undefined') {
      return false;
    }

    try {
      const userData = localStorage.getItem('aps_user_data');
      if (!userData) {
        return false;
      }

      const parsedUser = JSON.parse(userData);
      const lastChanceHospitalId = parsedUser.hospital_id || 
                                   parsedUser.hospitalId || 
                                   parsedUser.hospital?.hospital_id || 
                                   parsedUser.id;

      if (lastChanceHospitalId) {
        console.log('🏥 [BookAmbulanceForm] Found hospital_id on last attempt:', lastChanceHospitalId);
        bookingData.hospital_id = String(lastChanceHospitalId);
        return true;
      }

      console.error('❌ [BookAmbulanceForm] hospital_id not found in localStorage');
      console.error('❌ [BookAmbulanceForm] User data keys:', Object.keys(parsedUser));
      console.error('❌ [BookAmbulanceForm] Full user data:', JSON.stringify(parsedUser, null, 2));
    } catch (error) {
      console.error('❌ [BookAmbulanceForm] Error reading localStorage:', error);
    }

    return false;
  };

  // Helper function to validate hospital ID for hospital users
  const validateHospitalIdForHospitalUser = (bookingData, user, hospitalId) => {
    if (!isHospitalUser(user) || bookingData.hospital_id) {
      return true;
    }

    console.error('❌ [BookAmbulanceForm] hospital_id is missing for hospital user!');
    console.error('❌ [BookAmbulanceForm] hospitalId from state:', hospitalId);
    console.error('❌ [BookAmbulanceForm] user.hospital_id:', user.hospital_id);
    console.error('❌ [BookAmbulanceForm] bookingData keys:', Object.keys(bookingData));

    if (tryGetHospitalIdFromLocalStorage(bookingData)) {
      return true;
    }

    setErrorState('❌ Hospital ID is missing. Please ensure you are logged in as a hospital user and try refreshing the page. If the issue persists, please contact support.');
    setShowError(true);
    return false;
  };

  // Helper function to create booking via appropriate service
  const createBookingViaService = async (bookingData, user) => {
    if (isHospitalUser(user)) {
      console.log('🏥 Using hospital service to create booking');
      return await hospitalService.createBooking(bookingData);
    }

    console.log('📋 Using standard API service to create booking');
    return await apiService.createBooking(bookingData);
  };

  // Helper function to handle booking success
  const handleBookingSuccess = (result, bookingId, formData, user, selectedPartnerHospitalId, partnerHospitals, selectedHospitalId, hospitalId, availableHospitals, hospitalData, ambulanceTypes) => {
    if (!bookingId) {
      const successData = buildSuccessData(
        "Contact Support",
        formData,
        user,
        selectedPartnerHospitalId,
        partnerHospitals,
        selectedHospitalId,
        hospitalId,
        availableHospitals,
        hospitalData,
        ambulanceTypes,
        null
      );
      showSuccessModal(successData);
      setSuccessfulBookingId("error-no-id");
      return;
    }

    setSuccessfulBookingId(bookingId);

    const paymentType = formData.paymentType || formData.payment_method;
    if (isOnlinePayment(paymentType)) {
      handlePaymentRedirect(bookingId);
      return;
    }

    const estimatedFare = extractEstimatedFare(result);
    const successData = buildSuccessData(
      bookingId,
      formData,
      user,
      selectedPartnerHospitalId,
      partnerHospitals,
      selectedHospitalId,
      hospitalId,
      availableHospitals,
      hospitalData,
      ambulanceTypes,
      estimatedFare
    );

    showSuccessModal(successData);
  };

  // New function to handle actual booking after confirmation
  const handleConfirmBooking = async () => {
    if (setIsLoading) {
      setIsLoading(true);
    }

    hideConfirmationModal();

    try {
      console.log("🔍 Form submission started - checking coordinates...");
      console.log("pickupLocation state:", pickupLocation);
      console.log("destinationLocation state:", destinationLocation);

      if (!validateAndLogLocation(pickupLocation, 'Pickup')) {
        return;
      }

      if (!validateAndLogLocation(destinationLocation, 'Destination')) {
        return;
      }

      console.log("✅ Coordinates validated successfully:", {
        pickup: pickupLocation,
        destination: destinationLocation,
      });

      await getCoordinates(formData.pickupAddress);
      await getCoordinates(formData.destinationAddress);

      const bookingData = buildBookingData(
        formData,
        user,
        hospitalId,
        selectedPartnerHospitalId,
        pickupLocation,
        destinationLocation,
        selectedFleetPartners
      );

      console.log("📤 Sending booking data to API:", JSON.stringify(bookingData, null, 2));
      console.log("🏥 Hospital ID in booking data:", bookingData.hospital_id);
      console.log("🏥 User role:", user?.role);
      console.log("🏥 hospitalId from state:", hospitalId);
      console.log("pickupLocation in bookingData:", bookingData.pickupLocation);
      console.log("dropoffLocation in bookingData:", bookingData.dropoffLocation);

      if (!validateHospitalIdForHospitalUser(bookingData, user, hospitalId)) {
        if (setIsLoading) {
          setIsLoading(false);
        }
        return;
      }

      // Get selected ambulance type for driver filtering
      const selectedAmbulanceType = ambulanceTypes.find(
        (type) => type.value === formData.ambulanceType
      );

      if (!selectedAmbulanceType) {
        setErrorState('❌ Please select an ambulance type');
        setShowError(true);
        if (setIsLoading) {
          setIsLoading(false);
        }
        return;
      }

      // Start automatic allocation flow as default
      console.log('🔄 Starting automatic allocation flow...');
      
      // Fetch available drivers based on booking criteria
      const availableDrivers = await fetchAvailableDrivers(bookingData, selectedAmbulanceType);
      
      if (availableDrivers.length === 0) {
        // No drivers available - proceed with regular booking
        console.log('⚠️ No drivers available for automatic allocation, proceeding with regular booking');
        
        const result = await createBookingViaService(bookingData, user);
        console.log('📥 Booking creation response:', result);

        const bookingId = extractBookingId(result);
        handleBookingSuccess(
          result,
          bookingId,
          formData,
          user,
          selectedPartnerHospitalId,
          partnerHospitals,
          selectedHospitalId,
          hospitalId,
          availableHospitals,
          hospitalData,
          ambulanceTypes
        );
      } else {
        // Show automatic allocation modal
        console.log('✅ Starting automatic allocation with', availableDrivers.length, 'available drivers');
        showAutomaticAllocationModal(bookingData, selectedAmbulanceType, availableDrivers);
      }
      
    } catch (err) {
      handleBookingError(err, setErrorState, setShowError, hideSuccessModal);
    } finally {
      if (setIsLoading) {
        setIsLoading(false);
      }
    }
  };

  // Handle automatic allocation completion
  const handleAutomaticAllocationComplete = async (allocationResult) => {
    console.log('✅ Automatic allocation completed:', allocationResult);
    hideAutomaticAllocationModal();
    
    try {
      // Create the booking with the allocated driver information
      const bookingDataWithDriver = {
        ...allocationResult.bookingData,
        allocatedDriver: allocationResult.driver,
        ambulanceDetails: allocationResult.ambulance,
        allocationType: 'automatic'
      };

      const result = await createBookingViaService(bookingDataWithDriver, user);
      console.log('📥 Booking creation response with allocated driver:', result);

      const bookingId = extractBookingId(result);
      
      // Build success data with driver information
      const estimatedFare = extractEstimatedFare(result);
      const successData = buildSuccessData(
        bookingId,
        formData,
        user,
        selectedPartnerHospitalId,
        partnerHospitals,
        selectedHospitalId,
        hospitalId,
        availableHospitals,
        hospitalData,
        ambulanceTypes,
        estimatedFare
      );

      // Add driver information to success data
      successData.driverName = allocationResult.driver.name;
      successData.driverPhone = allocationResult.driver.phone;
      successData.ambulanceNumber = allocationResult.driver.ambulanceNumber;
      successData.allocationType = 'automatic';
      successData.message = 'Booking confirmed! Driver has been automatically assigned.';

      showSuccessModal(successData);
      setSuccessfulBookingId(bookingId);
      
    } catch (err) {
      handleBookingError(err, setErrorState, setShowError, hideSuccessModal);
    }
  };

  // Handle automatic allocation failure
  const handleAutomaticAllocationFailed = async (failureResult) => {
    console.log('❌ Automatic allocation failed:', failureResult);
    hideAutomaticAllocationModal();
    
    // Show error message and suggest manual booking
    setErrorState(
      `🔄 Automatic allocation failed: ${failureResult.reason}. You can try booking again or contact support for manual assistance.`
    );
    setShowError(true);
  };
  const selectedAmbulance = ambulanceTypes.find(
    (type) => type.value === formData.ambulanceType
  );

  // Button activation logic based on ambulance type selection
  // As per requirements: Button disabled by default, enabled only when ambulance type is selected
  const isBookEnabled = Boolean(formData.ambulanceType);
  
  // Debug logging to help troubleshoot button activation
  console.log('🔍 Button activation debug:', {
    ambulanceType: formData.ambulanceType,
    isBookEnabled,
    ambulanceTypesLength: ambulanceTypes.length,
    selectedAmbulance: selectedAmbulance?.label || 'none',
    loadingStates: {
      isLoading,
      loadingHospitalId,
      loadingAmbulanceTypes
    }
  });

  // Show loading state while fetching hospital ID
  if (loadingHospitalId) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading hospital information...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Truck className="w-5 h-5 text-purple-600" />
          <h2 className="text-xl font-semibold text-gray-900">
            Book Ambulance
          </h2>
        </div>
        <p className="text-sm text-gray-600">
          Book ambulances instantly or schedule for later. All bookings include
          real-time tracking and automatic driver allocation.
        </p>
        <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
            <p className="text-sm text-blue-800 font-medium">
              🔄 Automatic Allocation Enabled
            </p>
          </div>
          <p className="text-xs text-blue-700 mt-1">
            The system will automatically find and assign the nearest available driver based on your selected criteria.
          </p>
        </div>
        {user && (
          <p className="text-xs text-gray-500 mt-2">
            Booking for: <span className="font-medium">{user.name}</span>
            {user.hospital_id && (
              <span> (Hospital ID: {user.hospital_id})</span>
            )}
          </p>
        )}
      </div>

      {/* Success Modal */}
      <SuccessModal
        isOpen={successModal.isOpen}
        onClose={() => hideSuccessModal(true)}
        bookingId={successModal.data?.bookingId}
        patientName={successModal.data?.patientName}
        hospitalName={successModal.data?.hospitalName}
        ambulanceType={successModal.data?.ambulanceType}
        estimatedFare={successModal.data?.estimatedFare}
        finalFare={successModal.data?.finalFare}
        driverStatus={successModal.data?.driverStatus}
        driverName={successModal.data?.driverName}
        contactPhone={successModal.data?.contactPhone}
        contactPhone_country_code={successModal.data?.contactPhone_country_code}
        contactEmail={successModal.data?.contactEmail}
        pickupAddress={successModal.data?.pickupAddress}
        destinationAddress={successModal.data?.destinationAddress}
        bookingType={successModal.data?.bookingType}
        direction={successModal.data?.direction}
        onTrackBooking={handleTrackBooking}
        matchingStatus={successModal.data?.matchingStatus}
        paymentStatus={successModal.data?.paymentStatus}
        paymentId={successModal.data?.paymentId}
        message={successModal.data?.message}
      />

      {/* Confirmation Modal */}
      <AmbulanceBookingConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={hideConfirmationModal}
        onConfirm={handleConfirmBooking}
        patientName={confirmationModal.data?.patientName}
        hospitalName={confirmationModal.data?.hospitalName}
        ambulanceType={confirmationModal.data?.ambulanceType}
        estimatedFare={confirmationModal.data?.estimatedFare}
        patientAge={confirmationModal.data?.patientAge}
        phoneNumber={confirmationModal.data?.phoneNumber}
        hmisId={confirmationModal.data?.hmisId}
        pickupAddress={confirmationModal.data?.pickupAddress}
        destinationAddress={confirmationModal.data?.destinationAddress}
        currentCondition={confirmationModal.data?.currentCondition}
        emergencyLevel={confirmationModal.data?.emergencyLevel}
      />

      {/* Automatic Allocation Modal */}
      <AutomaticAllocationModal
        isOpen={automaticAllocationModal.isOpen}
        onClose={hideAutomaticAllocationModal}
        bookingData={automaticAllocationModal.bookingData}
        selectedAmbulance={automaticAllocationModal.selectedAmbulance}
        availableDrivers={automaticAllocationModal.availableDrivers}
        onAllocationComplete={handleAutomaticAllocationComplete}
        onAllocationFailed={handleAutomaticAllocationFailed}
      />

      {/* Error Message */}
      {showError && error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <svg
              className="w-5 h-5 text-red-600 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-sm text-red-700 font-medium">
              {typeof error === "string"
                ? error
                : error?.message || error?.error || "An error occurred"}
            </p>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Partner Hospital Selection for Fleet Users */}
        {user?.role === "TENANT_ADMIN" && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Select Partner Hospital (Optional)
            </label>
            {loadingPartnerHospitals ? (
              <div className="flex items-center justify-center p-4 border border-gray-300 rounded-md">
                <Loader2 className="w-4 h-4 animate-spin text-blue-600 mr-2" />
                <span className="text-gray-600 text-sm">Loading partner hospitals...</span>
              </div>
            ) : (
              <Select
                value={selectedPartnerHospitalId || ""}
                onValueChange={(value) => {
                  setSelectedPartnerHospitalId(value);
                  const ph = partnerHospitals.find((h) => (h.hospital_id || h.id) === value);
                  if (ph) {
                    // Auto-fill pickup address and coordinates when available
                    const address = ph.address || ph.location_address || ph.name;
                    handleInputChange("pickupAddress", address || "");
                    if (ph.latitude && ph.longitude) {
                      setPickupLocation({ latitude: ph.latitude, longitude: ph.longitude, address });
                    } else if (ph.location && ph.location.latitude && ph.location.longitude) {
                      setPickupLocation({ latitude: ph.location.latitude, longitude: ph.location.longitude, address });
                    }
                  }
                }}
              >
                <SelectTrigger className="border-gray-300 focus:ring-blue-500 focus:border-blue-500">
                  <SelectValue placeholder="Select partner hospital" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {partnerHospitals.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      <p className="text-sm">No partner hospitals found</p>
                    </div>
                  ) : (
                    partnerHospitals.map((h) => (
                      <SelectItem key={h.hospital_id || h.id} value={h.hospital_id || h.id}>
                        <div className="flex flex-col">
                          <span className="font-medium">{h.name}</span>
                          {(h.address || h.location_address) && (
                            <span className="text-xs text-gray-500">{h.address || h.location_address}</span>
                          )}
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            )}
            {selectedPartnerHospitalId && (
              <p className="text-xs text-gray-500">Pickup auto-filled from selected hospital. You can change it if needed.</p>
            )}
          </div>
        )}
        {/* Hospital Selection for SUPER_ADMIN */}
        {user?.role === "SUPER_ADMIN" && availableHospitals.length > 0 && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Select Hospital <span className="text-red-500">*</span>
            </label>
            <Select
              value={selectedHospitalId}
              onValueChange={(value) => {
                setSelectedHospitalId(value);
                setHospitalId(value);
              }}
            >
              <SelectTrigger className="border-gray-300 focus:ring-blue-500 focus:border-blue-500">
                <SelectValue placeholder="Select hospital">
                  {selectedHospitalId
                    ? availableHospitals.find(
                        (h) => h.hospital_id === selectedHospitalId
                      )?.name || "Unknown Hospital"
                    : "Select hospital"}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {availableHospitals.map((hospital) => (
                  <SelectItem
                    key={hospital.hospital_id}
                    value={hospital.hospital_id}
                  >
                    <div className="flex flex-col">
                      <span className="font-medium">{hospital.name}</span>
                      <span className="text-sm text-gray-500">
                        {hospital.address}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-sm text-gray-600">
              Currently selected:{" "}
              <span className="font-medium">
                {availableHospitals.find(
                  (h) => h.hospital_id === selectedHospitalId
                )?.name || "None"}
              </span>
            </div>
          </div>
        )}

        {/* Booking Type and Ambulance Type */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Booking Type
            </label>
            <Select
              value={formData.bookingType}
              onValueChange={(value) => handleInputChange("bookingType", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select booking type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="instant">Instant Booking</SelectItem>
                <SelectItem value="scheduled">Scheduled Booking</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ambulance Type
            </label>
            {loadingAmbulanceTypes ? (
              <div className="flex items-center justify-center p-4 border border-gray-300 rounded-md">
                <Loader2 className="w-4 h-4 animate-spin text-blue-600 mr-2" />
                <span className="text-gray-600">
                  Loading ambulance types...
                </span>
              </div>
            ) : (
              <Select
                value={
                  formData.ambulanceType
                    ? ambulanceTypes.find(
                        (type) => type.value === formData.ambulanceType
                      )?.label
                    : ""
                }
                onValueChange={(value) =>
                  handleInputChange("ambulanceType", value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select ambulance type" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {ambulanceTypes.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      <p className="text-sm">No ambulance types available</p>
                    </div>
                  ) : (
                    ambulanceTypes.map((type) => (
                      <SelectItem key={type.value} value={type.label}>
                        <div className="flex items-center justify-between w-full" title={type.fullName || type.label}>
                          <div className="flex-1">
                            <div className="font-semibold text-sm">
                              {type.label}
                            </div>
                            {type.description && (
                              <div className="text-xs text-gray-500 mt-1">
                                {type.description}
                              </div>
                            )}
                          </div>
                          <Badge variant="outline" className="ml-2 text-xs">
                            {type.price}
                          </Badge>
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            )}
            {selectedAmbulance && (
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-900">
                      {selectedAmbulance.label}
                    </p>
                    <p className="text-xs text-blue-700">
                      {selectedAmbulance.price}
                    </p>
                  </div>
                  <Badge className="bg-blue-100 text-blue-800">Selected</Badge>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <p className="text-xs text-green-700 font-medium">
                    Book Ambulance button is now enabled
                  </p>
                </div>
              </div>
            )}
            {!selectedAmbulance && ambulanceTypes.length > 0 && (
              <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full border-2 border-gray-400"></div>
                  <p className="text-xs text-gray-600">
                    Select an ambulance type to enable booking
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Fleet Partner Selection - Only for Hospital Users */}
        {user?.role === "HOSPITAL_USER" && partnerFleets.length > 0 && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Select Fleet Partners (Optional)
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Choose specific fleet operators for this booking. Leave empty to allow any partnered fleet.
            </p>
            {loadingPartnerFleets ? (
              <div className="flex items-center justify-center p-4 border border-gray-300 rounded-md">
                <Loader2 className="w-4 h-4 animate-spin text-blue-600 mr-2" />
                <span className="text-gray-600 text-sm">Loading partner fleets...</span>
              </div>
            ) : (
              <div className="space-y-2">
                {partnerFleets.map((fleet) => (
                  <label
                    key={fleet.id || fleet.fleet_id || fleet.operator_id}
                    className="flex items-center space-x-2 p-3 border rounded-md hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedFleetPartners.includes(fleet.id || fleet.fleet_id || fleet.operator_id)}
                      onChange={(e) => {
                        const fleetId = fleet.id || fleet.fleet_id || fleet.operator_id;
                        if (e.target.checked) {
                          setSelectedFleetPartners([...selectedFleetPartners, fleetId]);
                        } else {
                          setSelectedFleetPartners(selectedFleetPartners.filter(id => id !== fleetId));
                        }
                      }}
                      className="rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-gray-900">{fleet.name}</span>
                      {fleet.city && (
                        <span className="text-xs text-gray-500 ml-2">
                          {fleet.city}{fleet.state ? `, ${fleet.state}` : ''}
                        </span>
                      )}
                    </div>
                  </label>
                ))}
                {selectedFleetPartners.length > 0 && (
                  <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs text-blue-800 font-medium mb-1">
                      Selected: {selectedFleetPartners.length} fleet partner(s)
                    </p>
                    <p className="text-xs text-blue-700">
                      Booking will only be sent to selected fleet partners
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {/* Scheduled Booking Fields */}
        {formData.bookingType === "scheduled" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Scheduled Date
              </label>
              <Input
                type="date"
                value={formData.scheduledDate}
                onChange={(e) =>
                  handleInputChange("scheduledDate", e.target.value)
                }
                min={new Date().toISOString().split("T")[0]}
                required={formData.bookingType === "scheduled"}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Scheduled Time
              </label>
              <Input
                type="time"
                value={formData.scheduledTime}
                onChange={(e) =>
                  handleInputChange("scheduledTime", e.target.value)
                }
                required={formData.bookingType === "scheduled"}
              />
            </div>
          </div>
        )}
        {/* Patient Information */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Patient Name <span className="text-red-500">*</span>
            </label>
            <Input
              placeholder="Enter patient name"
              value={formData.patientName}
              onChange={(e) => handleInputChange("patientName", e.target.value)}
              className={
                fieldErrors.patientName
                  ? "border-red-500 focus:border-red-500"
                  : ""
              }
              required
            />
            {fieldErrors.patientName && (
              <p className="mt-1 text-sm text-red-600">
                {fieldErrors.patientName}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Hospital <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Input
                value={(() => {
                  if (authLoading) return "Loading user information..."; // ADD THIS
                  if (loadingHospitalId) return "Loading hospital...";

                  // For SUPER_ADMIN, show selected hospital from available hospitals
                  if (
                    user?.role === "SUPER_ADMIN" &&
                    availableHospitals.length > 0
                  ) {
                    const hospital = availableHospitals.find(
                      (h) => h.hospital_id === selectedHospitalId
                    );
                    return hospital
                      ? `${hospital.name} - ${hospital.address}`
                      : "Select a hospital above";
                  }

                  // For regular users, show hospital name from hospitalData
                  if (hospitalData) {
                    return `${hospitalData.name} - ${
                      hospitalData.address || hospitalData.city || ""
                    }`;
                  }

                  return "No hospital assigned";
                })()}
                disabled
                className="bg-gray-50 cursor-not-allowed"
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                <svg
                  className="w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h2M7 7h.01M7 11h.01M7 15h.01"
                  />
                </svg>
              </div>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {user?.role === "SUPER_ADMIN"
                ? "Select hospital from the dropdown above"
                : "Hospital is automatically assigned based on your account"}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Patient Age <span className="text-red-500">*</span>
            </label>
            <Input
              type="number"
              placeholder="0"
              value={formData.patientAge}
              onChange={(e) => handleInputChange("patientAge", e.target.value)}
              min="0"
              max="120"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Name of Relative <span className="text-red-500">*</span>
            </label>
            <Input
              placeholder="Enter relative name"
              value={formData.relativeName}
              onChange={(e) =>
                handleInputChange("relativeName", e.target.value)
              }
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Patient HMIS ID <span className="text-red-500">*</span>
            </label>
            <Input
              placeholder="Enter patient HMIS ID"
              value={formData.hmisId}
              onChange={(e) => handleInputChange("hmisId", e.target.value)}
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              Hospital Management Information System ID for patient records
            </p>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Current Condition <span className="text-red-500">*</span>
          </label>
          <Textarea
            placeholder="Describe the patient current medical condition..."
            value={formData.currentCondition}
            onChange={(e) =>
              handleInputChange("currentCondition", e.target.value)
            }
            rows={3}
            required
          />
        </div>
        {/* Smart Contact Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Contact Information <span className="text-red-500">*</span>
          </label>
          <Input
            placeholder="Enter phone number or email"
            value={formData.contactPhone || formData.contactEmail || ""}
            onChange={(e) => {
              const input = e.target.value;
              const detection = detectContactType(input);

              if (detection.type === "email") {
                setFormData((prev) => ({
                  ...prev,
                  contactEmail: detection.email,
                  contactPhone: "",
                  contactPhone_country_code: "+91",
                }));
              } else if (detection.type === "phone") {
                setFormData((prev) => ({
                  ...prev,
                  contactPhone: detection.phone,
                  contactPhone_country_code: detection.countryCode,
                  contactEmail: "",
                }));
              } else {
                // If user is typing but no clear pattern, keep the raw input
                setFormData((prev) => ({
                  ...prev,
                  contactPhone: input,
                  contactEmail: "",
                }));
              }
            }}
            required
          />
          <div className="mt-2 flex gap-2">
            {formData.contactPhone && (
              <div className="flex items-center gap-2">
                <div className="w-32">
                  <CountryCodeSelector
                    value={formData.contactPhone_country_code}
                    onChange={(e) => {
                      const newCountryCode = e.target.value;
                      handleInputChange(
                        "contactPhone_country_code",
                        newCountryCode
                      );
                      const countryIso =
                        countryCodeToIso[newCountryCode] || "in";
                      setSelectedCountryIso(countryIso);
                    }}
                    disabled={propIsLoading}
                    placeholder="Country"
                  />
                </div>
                <div className="flex-1">
                  <Input
                    placeholder="Phone number"
                    value={formData.contactPhone}
                    onChange={(e) =>
                      handleInputChange("contactPhone", e.target.value)
                    }
                  />
                </div>
              </div>
            )}
            {formData.contactEmail && (
              <div className="flex-1">
                <Input
                  placeholder="Email address"
                  value={formData.contactEmail}
                  onChange={(e) =>
                    handleInputChange("contactEmail", e.target.value)
                  }
                />
              </div>
            )}
          </div>
          <p className="mt-1 text-xs text-gray-500">
            {formData.contactPhone
              ? "Phone detected. Location search will be filtered by selected country."
              : formData.contactEmail
              ? "Email detected. You can also enter a phone number."
              : "Enter phone number or email address. We'll auto-detect the format."}
          </p>
        </div>
        {/* Address Information */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <MapPin className="w-4 h-4 inline mr-1" />
              Pickup Address <span className="text-red-500">*</span>
            </label>
            <LocationAutocomplete
              value={formData.pickupAddress}
              onChange={(e) =>
                handleInputChange("pickupAddress", e.target.value)
              }
              onLocationSelect={(location) => {
                setPickupLocation(location);
                if (location) {
                  handleInputChange("pickupAddress", location.address);
                }
              }}
              placeholder="Search for pickup location..."
              country={selectedCountryIso}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <MapPin className="w-4 h-4 inline mr-1" />
              Destination Address <span className="text-red-500">*</span>
            </label>
            <LocationAutocomplete
              value={formData.destinationAddress}
              onChange={(e) =>
                handleInputChange("destinationAddress", e.target.value)
              }
              onLocationSelect={(location) => {
                setDestinationLocation(location);
                if (location) {
                  handleInputChange("destinationAddress", location.address);
                }
              }}
              placeholder="Search for destination..."
              country={selectedCountryIso}
              required
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Emergency Level
          </label>
          <Select
            value={formData.emergencyLevel}
            onValueChange={(value) =>
              handleInputChange("emergencyLevel", value)
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select emergency level" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="normal">Normal Priority</SelectItem>
              <SelectItem value="urgent">Urgent Priority</SelectItem>
              <SelectItem value="emergency">Emergency Priority</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Special Requirements (Optional)
          </label>
          <Textarea
            placeholder="Any special medical equipment or requirements..."
            value={formData.specialRequirements}
            onChange={(e) =>
              handleInputChange("specialRequirements", e.target.value)
            }
            rows={3}
          />
        </div>
        {/* Error display moved to top of form */}
        <div className="flex justify-center">
          <Button
            type="submit"
            disabled={
              isLoading || 
              loadingHospitalId || 
              loadingAmbulanceTypes || 
              !isBookEnabled // Button disabled if no ambulance type selected
            }
            className={`w-full sm:w-auto px-8 py-3 font-medium rounded-md transition-all duration-200 ${
              !isBookEnabled
                ? 'bg-gray-400 cursor-not-allowed text-gray-600' // Disabled state styling
                : 'bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 disabled:cursor-not-allowed text-white'
            }`}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing Booking...
              </>
            ) : loadingHospitalId ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Loading Hospital Info...
              </>
            ) : loadingAmbulanceTypes ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Loading Ambulance Types...
              </>
            ) : !isBookEnabled ? (
              <>
                <CheckCircle className="w-4 h-4 mr-2 opacity-50" />
                Select Ambulance Type First ({formData.ambulanceType || 'empty'})
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Book with Auto-Allocation ✓
              </>
            )}
          </Button>
        </div>
        
        {/* Debug info - remove after testing */}
        <div className="mt-2 text-center text-xs text-gray-500">
          Debug: ambulanceType="{formData.ambulanceType}" | isBookEnabled={isBookEnabled.toString()} | 
          loading={loadingAmbulanceTypes.toString()}
        </div>
        {!isAuthenticated && (
          <div className="text-center p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-700">
              Please login to book an ambulance
            </p>
          </div>
        )}
      </form>
      {successfulBookingId && (
        <div className="mt-8">
          <BookingTrackingCard
            bookingId={successfulBookingId}
            journeyDirection={formData.direction?.toUpperCase() || successModal.data?.direction?.toUpperCase() || 'OUTBOUND'}
            onClose={() => setSuccessfulBookingId(null)}
          />

          {/* Emergency Contact Card */}
          <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <svg
                  className="w-5 h-5 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-red-900">
                Emergency Contact
              </h3>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-red-700">
                  Emergency Helpline:
                </span>
                <span className="font-semibold text-red-900">108</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-red-700">Ambulance Service:</span>
                <span className="font-semibold text-red-900">
                  +91-9876543210
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-red-700">Police:</span>
                <span className="font-semibold text-red-900">100</span>
              </div>
            </div>
            <div className="mt-4 p-3 bg-red-100 rounded-lg">
              <p className="text-xs text-red-800">
                <strong>Note:</strong> In case of emergency, call these numbers
                immediately. Our booking system is for non-emergency medical
                transport.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default BookAmbulanceForm;
